import {
	AggregatedMeasurementStatus,
	AggregatedStatusConfig,
} from "@/components/Procedures/Checklist/utils/Types";
import { calculateButtonStyle, calculateStatusListenerTagConfig } from "@/components/Procedures/UpdatedProceduresDetails";

import {
	ProcedureListenerMeasurement,
	ProcedureListenerTag,
} from "@/model/Procedure/ProcedureListenerTag";
import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";
import { ProcedureNodeSteps } from "@/model/Procedure/ProcedureNodeSteps";

export const isMultipleFieldStep = (numberOfFields: number) => {
	return numberOfFields > 0;
};

export const isHeader = (step: ProcedureNodeStep) => {
	return step.Index === 1 && isMultipleFieldStep(step.NumberOfFields);
};

export const buildStepWithActiveListenerTags = (
	step: ProcedureNodeStep,
	activeListenerTags: ProcedureListenerTag[] | undefined,
): ProcedureNodeStep => {
	const stepWithListenerTags = {
		...step,
		ActiveListenerTags: activeListenerTags ?? [],
	};

	return stepWithListenerTags;
};

export const getRowTypeClass = (step: ProcedureNodeStep) => {
	const ribbon = step.IsSkipped ? "ribbon ribbonGrey" : step.IsOverridden ? "ribbon ribbonOrange" : "";
	return step?.ChecklistType === "Info"
		? "grid-row grid-row-heading grid-row-heading-info"
		: `grid-row grid-row-item ${ribbon}`;
};

export const getColumnTypeClass = (step: ProcedureNodeStep) => {
	return step?.ChecklistType === "Info"
		? "grid-column grid-column--info"
		: step.NumberOfFields === 0
			? "grid-column grid-column--chapter"
			: "grid-column grid-column--imported";
};

export const aggregateStatus = (
	readings: ProcedureListenerMeasurement[],
): AggregatedMeasurementStatus => {
	let aggregatedStatus: AggregatedMeasurementStatus = "ok";

	for (const { value, status } of readings) {
		if (Number.isNaN(value)) {
			aggregatedStatus = "wait";
			continue;
		}

		if (!status) {
			aggregatedStatus = "alert";
			break;
		}

		aggregatedStatus = "ok";
	}

	return aggregatedStatus;
};

export const calculateStatusIconConfig = (
	aggregatedStatus: AggregatedMeasurementStatus | undefined,
): AggregatedStatusConfig => {
	switch (aggregatedStatus) {
		case "alert":
			return {
				button: "button-icon button--red",
				svg: "svg-icon svg-icon-white svg-icon--signal",
			};
		case "ok":
			return {
				button: "button-icon button--green",
				svg: "svg-icon svg-icon-white svg-icon--signal",
			};
		default:
			return {
				button: "button-icon button--yellow",
				svg: "svg-icon svg-icon-dark svg-icon--signal",
			};
	}
};

export const validSchemeId = (schemeId: number | undefined) => {
	return schemeId !== undefined && !isNaN(schemeId);
};

export function stringToColor(string: string) {
	let hash = 0;
	let i;
	 
	for (i = 0; i < string.length; i += 1) {
		hash = string.charCodeAt(i) + ((hash << 5) - hash);
	}
	let color = "#";
	for (i = 0; i < 3; i += 1) {
		const value = (hash >> (i * 8)) & 0xff;
		color += `00${value.toString(16)}`.slice(-2);
	}
	 
	return color;
}

export function getTextColorForBackground(hexColor: string): string {
	// Remove the hash at the start if it's there
	hexColor = hexColor.replace(/^#/, '');

	// Parse the r, g, b values
	const r = parseInt(hexColor.substring(0, 2), 16);
	const g = parseInt(hexColor.substring(2, 4), 16);
	const b = parseInt(hexColor.substring(4, 6), 16);

	// Calculate the luminance
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

	// Return black for light backgrounds and white for dark backgrounds
	return luminance > 0.5 ? '#000000' : '#FFFFFF';
}


export function stringAvatar(name: string | undefined) {
	if(!name || name === "Anonymous") {
		return {
			sx: {
				bgcolor: stringToColor("AA"),
				color: getTextColorForBackground(stringToColor("AA")),
			},
			children: "AA",
		};
	}
	const initials = name.split(" ");
	let children = initials[0][0]; // Always take the first character of the first word

	// Check if there is a second word and if so, add its first character
	if (initials.length > 1 && initials[1]) {
		children += initials[1][0];
	}

	return {
		sx: {
			bgcolor: stringToColor(name),
			color: getTextColorForBackground(stringToColor(name)),
		},
		children: children,
	};
}

export const successTagStyle = calculateStatusListenerTagConfig(
	AggregatedMeasurementStatus.Ok,
);
export const waitTagStyle = calculateStatusListenerTagConfig(
	AggregatedMeasurementStatus.Wait,
);
export const alertTagStyle = calculateStatusListenerTagConfig(
	AggregatedMeasurementStatus.Alert,
);
export const defaultTagStyle = calculateStatusListenerTagConfig(
	AggregatedMeasurementStatus.Default,
);

export const successButtonStyle = calculateButtonStyle(
	AggregatedMeasurementStatus.Ok,
);
export const waitButtonStyle = calculateButtonStyle(
	AggregatedMeasurementStatus.Wait,
);
export const alertButtonStyle = calculateButtonStyle(
	AggregatedMeasurementStatus.Alert,
);
export const defaultButtonStyle = calculateButtonStyle(
	AggregatedMeasurementStatus.Default,
);

export const isStepActive = (
	currentStep: ProcedureNodeStep,
	activeNodeSteps: ProcedureNodeSteps | undefined,
) => {
	if (!Number.isNaN(currentStep.Status) && currentStep.Status) {
		return false;
	} else {
		return getLatestCheckStatus(currentStep, activeNodeSteps);
	}
};

export const getLatestCheckStatus = (
	currentStep: ProcedureNodeStep,
	activeNodeSteps: ProcedureNodeSteps | undefined,
) => {
	if (currentStep && activeNodeSteps) {
		const checkSteps = activeNodeSteps?.Steps.filter(
			(step) => step.ChecklistType === "Check",
		);

		const currentStepIndex = activeNodeSteps?.Steps.findIndex(
			(x) => x.Id === currentStep.Id,
		);

		for (let i = currentStepIndex - 1; i >= 0; i--) {
			if (activeNodeSteps?.Steps[i].IsSkipped) {
				if (i === 0) {
					return true;
				}
				continue;
			}
			const previousStepStatus = Boolean(activeNodeSteps?.Steps[i].Status);

			return previousStepStatus;
		}
	}
	return false;
};
